import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Ban, CheckCheck, Clock3, Coins, FileClock, Gift, HandCoins, Info,
  ListChecks, Ticket, Trophy, User, Wand2, XCircle,
} from 'lucide-react';
import client from '../lib/api';
import ConfirmDialog from './components/ConfirmDialog';
import { AdminError, AdminSkeleton } from './components/PageState';
import {
  DetailPage, DetailTop, Empty, FlowList, HeroCard, LegCard, MemberCard, Note,
  Panel, Tag, badgeTone,
} from './components/BetDetailKit';
import { formatDateTime, formatMoney } from './lib/formatters';
import { useAdminPermissions } from './lib/permissions';
import {
  LEDGER_ROLE_LABELS, PICK_RESULT_LABELS, PROVIDER_LABELS, SETTLE_ACTIONS,
  SETTLE_PERMISSION, STATUS_LABELS, pickLabel, settleAction, settleSummaryText,
} from './lib/sportsBets';
import './betDetail.css';

// Tek kuponun tam dökümü.
//
// Kupon üç ayrı tabloya dağılmıştır: kuponun kendisi ``coupons``, para
// hareketleri ``transactions``, yönetici müdahalesi ``admin_audit_logs``.
// Yönetici "bu kupona ne oldu" sorusunu yanıtlamak için üçüne birden bakmak
// zorundaydı; bu sayfa hepsini bir araya getirir.
//
// Para hareketleri kupon numarasıyla **aranmaz**: üç satırın da kimliği kupon
// id'sinden uuid5 ile türetilir ve backend rollerini yazar (bahis, kazanç,
// cashout). Bahis satırının notunda kupon numarası hiç yazmaz, metin araması
// onu bulamazdı.
//
// Düzen hazır spor kitabının kupon sayfasıyla **ortaktır**
// (`components/BetDetailKit`): yönetici için tek bir soru vardır ve iki ayrı
// tasarım aynı bilgiyi iki ekranda iki ayrı yerde arattırırdı. Yerleşim
// `betDetail.css` içindedir.

// Bacağın sonucu kartın sol şeridini boyar. "Bekliyor" bilinçli olarak sarıdır
// — gri, sonuçlanmış ama iade edilmiş bacakla karışıyordu.
const LEG_TONES = { won: 'ok', lost: 'bad', void: 'gray' };

const STATUS_ICONS = {
  won: Trophy, lost: XCircle, cashed_out: HandCoins, active: Clock3,
};

// Künye kartının altındaki tek cümle: rozet "ne oldu"yu söyler, bu satır
// "parası ne oldu"yu. İkisi ayrı sorulardır — kapanmış kupon ödeme yapılmış
// demek değildir.
const STATUS_NOTES = {
  won: 'Kupon kazandı; ödeme bakiyeye eklendi.',
  lost: 'Kupon kaybetti; kasadan ödeme yapılmadı.',
  cashed_out: 'Oyuncu kuponu erken kapattı (cashout).',
  active: 'Kupon henüz sonuçlanmadı. Spor kuponu günlerce açık kalabilir.',
};

const SETTLE_TONES = { won: 'ok', lost: 'bad', void: 'warn' };

const SportsBetDetail = () => {
  const { couponId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Tekil sonuçlandırma listedeki toplu işlemle **aynı ucu** kullanır; iki
  // yerde iki farklı para mantığı doğmasın diye.
  const [settleRequest, setSettleRequest] = useState(null);
  const [settling, setSettling] = useState(false);
  const [settleNote, setSettleNote] = useState('');
  const [settleResult, setSettleResult] = useState('');
  const { can } = useAdminPermissions();
  const canSettle = can(SETTLE_PERMISSION);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await client.get(`/admin/coupons/${couponId}`);
      setData(response.data);
    } catch (fault) {
      setData(null);
      setError(
        fault?.response?.status === 404
          ? 'Kupon bulunamadı. Silinmiş ya da numarası yanlış olabilir.'
          : 'Kupon detayı yüklenemedi.',
      );
    } finally {
      setLoading(false);
    }
  }, [couponId]);

  useEffect(() => { load(); }, [load]);

  const runSettle = async () => {
    if (!settleRequest) return;
    setSettling(true);
    try {
      const { data: result } = await client.post('/admin/coupons/bulk-settle', {
        coupon_ids: [couponId],
        action: settleRequest,
        note: settleNote.trim(),
      });
      setSettleResult(settleSummaryText(result));
      setSettleRequest(null);
      setSettleNote('');
      await load();
    } catch (fault) {
      setSettleResult(
        fault?.response?.data?.detail || 'Kupon sonuçlandırılamadı.');
    } finally {
      setSettling(false);
    }
  };

  if (loading) return <AdminSkeleton label="Kupon yükleniyor" />;
  if (error) return <AdminError message={error} onRetry={load} />;
  if (!data) return null;

  const { coupon, user, transactions = [], audit = [], freebet } = data;
  const results = data.event_results || {};
  const badge = STATUS_LABELS[coupon.status] || STATUS_LABELS.active;
  const tone = badgeTone(badge.cls);
  const settled = coupon.status === 'won' || coupon.status === 'cashed_out';
  const StatusIcon = STATUS_ICONS[coupon.status] || Clock3;
  const customLegs = coupon.picks.filter((pick) => pick.custom_odd).length;

  return (
    <DetailPage tone={tone}>
      <DetailTop
        icon={Ticket}
        title="Kupon detayı"
        backTo="/admin/sports-bets"
        onRefresh={load}
        refreshing={loading}
        tags={(
          <>
            <Tag kind="status">{badge.label}</Tag>
            {coupon.freebet && (
              <Tag
                kind="freebet"
                title="Freebet ile oynandı: bakiyeden para düşülmedi ve kazançta bahis tutarı iade edilmez"
              >
                <Gift size={12} aria-hidden="true" /> Freebet
              </Tag>
            )}
            {customLegs > 0 && (
              <Tag kind="magic" title="Panelden geçirilmiş özel oranla oynanmış bacak">
                <Wand2 size={12} aria-hidden="true" />
                {customLegs} özel oran
              </Tag>
            )}
          </>
        )}
      />

      {/* Sonuçlandırma yalnızca açık kuponda sunulur: kapanmış kupon zaten
          atlanır, düğmeyi göstermek yöneticiye yapılamayan bir iş vaat eder. */}
      {canSettle && coupon.status === 'active' && (
        <Note
          tone="info"
          icon={Info}
          actions={SETTLE_ACTIONS.map((action) => (
            <button
              key={action.value}
              type="button"
              onClick={() => {
                setSettleResult('');
                setSettleNote('');
                setSettleRequest(action.value);
              }}
              className={`bdet-act is-${SETTLE_TONES[action.value] || 'ok'}`}
            >
              {action.label}
            </button>
          ))}
        >
          <b>Kupon sonuçlanmadı.</b> Otomatik sonuçlandırma çalışmadıysa elle
          kapatabilirsiniz; karar para hareketi doğurur ve denetim izine yazılır.
        </Note>
      )}

      {settleResult && (
        <Note tone="warn" icon={CheckCheck}>{settleResult}</Note>
      )}

      <HeroCard
        icon={StatusIcon}
        statusLabel={badge.label}
        statusNote={STATUS_NOTES[coupon.status] || STATUS_NOTES.active}
        idLabel={coupon.id}
        idTitle={`Kupon numarası: ${coupon.id}`}
        metrics={[
          {
            label: 'Bahis',
            value: (
              <>
                {formatMoney(coupon.stake)}
                {coupon.freebet && <Tag kind="freebet">Freebet</Tag>}
              </>
            ),
          },
          {
            label: 'Toplam oran',
            value: Number(coupon.total_odd).toFixed(2),
          },
          {
            label: settled ? 'Ödeme' : 'Muhtemel kazanç',
            title: settled ? undefined : 'Kupon sonuçlanmadı',
            tone: settled && coupon.payout > 0 ? 'ok' : undefined,
            value: formatMoney(settled ? coupon.payout : coupon.possible_win),
            hint: settled ? undefined : 'Sonuçlanırsa ödenecek tutar',
          },
          {
            label: 'Net',
            title: 'Freebet kuponunda bahis kasadan çıkmadığı için nete girmez',
            tone: coupon.pnl >= 0 ? 'ok' : 'bad',
            value: formatMoney(coupon.pnl),
          },
          {
            label: 'Kupon tarihi',
            muted: true,
            value: formatDateTime(coupon.created_at),
          },
          {
            label: 'Sonuç tarihi',
            muted: true,
            value: coupon.settled_at ? formatDateTime(coupon.settled_at) : '—',
          },
        ]}
      />

      <Panel
        icon={ListChecks}
        title="Seçimler"
        subtitle={`${coupon.leg_count} bacak`}
        tone="#fbbf24"
      >
        <div className="bdet-legs">
          {coupon.picks.map((pick, index) => {
            const result = results[String(pick.match_id || '')];
            return (
              <LegCard
                // eslint-disable-next-line react/no-array-index-key
                key={`${coupon.id}-${index}`}
                index={index + 1}
                event={`${pick.home} - ${pick.away}`}
                meta={[
                  pick.league,
                  pick.start_time ? formatDateTime(pick.start_time) : null,
                ]}
                pickLabel={pickLabel(pick)}
                odd={Number(pick.odd || 0).toFixed(2)}
                oddExtra={pick.custom_odd ? (
                  /* Sağlayıcının ham fiyatı da saklanır: yöneticinin
                     müdahalesinin büyüklüğü kupon satırından okunmalı. */
                  <span
                    className="bdet-tag is-magic"
                    title={pick.base_odd
                      ? `Sağlayıcı fiyatı ${Number(pick.base_odd).toFixed(2)}`
                      : 'Panelden geçirilmiş özel oran'}
                  >
                    <Wand2 size={11} aria-hidden="true" />
                    {pick.base_odd ? Number(pick.base_odd).toFixed(2) : 'özel'}
                  </span>
                ) : null}
                score={pick.score}
                resultLabel={PICK_RESULT_LABELS[pick.result] || 'Bekliyor'}
                resultTone={LEG_TONES[pick.result] || 'warn'}
                source={PROVIDER_LABELS[pick.provider || 'apifootball']
                  || pick.provider || '—'}
                /* Panelden girilen sonuç sağlayıcınınkini ezer; bacağı kimin
                   kapattığı ancak burada görünür. */
                note={result ? (
                  <>
                    {result.void ? 'Panelden iptal' : 'Panelden girildi'}
                    {result.created_at
                      ? ` · ${formatDateTime(result.created_at)}` : ''}
                  </>
                ) : null}
              />
            );
          })}
        </div>
      </Panel>

      <div className="bdet-cols">
        <Panel icon={Coins} title="Para hareketleri" tone="#38bdf8">
          {transactions.length ? (
            <FlowList rows={transactions} labels={LEDGER_ROLE_LABELS} />
          ) : (
            <Empty>Bu kupona bağlı para hareketi yok.</Empty>
          )}
          {coupon.freebet && (
            <p className="bdet-hint">
              Freebet kuponunda bahis satırı <b>sıfır tutarlıdır</b>: para
              bakiyeden düşülmemiştir. Kazançta da bahis tutarı iade edilmez.
            </p>
          )}
        </Panel>

        <Panel icon={User} title="Üye" tone="#a78bfa">
          {user ? (
            <MemberCard user={user}>
              {user.is_banned && (
                <p className="bdet-banned">
                  <Ban size={13} aria-hidden="true" /> Hesap yasaklı
                </p>
              )}
              {freebet && (
                <p className="bdet-hint">
                  Kullanılan freebet hakkı: <b>{formatMoney(freebet.amount)}</b>
                  {freebet.title ? ` · ${freebet.title}` : ''}
                  {freebet.source ? ` · kaynak: ${freebet.source}` : ''}
                </p>
              )}
            </MemberCard>
          ) : (
            <Empty>Üye kaydı bulunamadı (hesap silinmiş olabilir).</Empty>
          )}
        </Panel>
      </div>

      <Panel
        icon={FileClock}
        title="İşlem izi"
        subtitle="yönetici müdahaleleri"
        tone="#f472b6"
      >
        {audit.length ? (
          <div className="bdet-flow">
            {audit.map((row) => (
              <div className="bdet-flow-row" key={row.id}>
                <span className="bdet-flow-mark">
                  <FileClock size={13} aria-hidden="true" />
                </span>
                <div className="bdet-flow-body">
                  <div className="bdet-flow-line">
                    <span className="bdet-flow-name">{row.action}</span>
                    <span className="bdet-flow-when">
                      {formatDateTime(row.created_at)}
                    </span>
                  </div>
                  <div className="bdet-flow-meta">
                    Yetkili <b>{row.admin_username || '—'}</b>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty>
            Bu kupona yönetici müdahalesi yapılmamış. Otomatik sonuçlandırma iz
            bırakmaz; kuponun kapanış zamanı künye kartındaki
            {' '}<b>Sonuç tarihi</b> alanındadır.
          </Empty>
        )}
      </Panel>

      <ConfirmDialog
        open={!!settleRequest}
        busy={settling}
        tone={settleRequest === 'won' ? 'warning' : 'danger'}
        title={settleRequest
          ? `Kuponu ${settleAction(settleRequest).label.toLowerCase()}` : ''}
        description={settleRequest
          ? `${settleAction(settleRequest).warning} Bu işlem geri alınamaz ve `
            + 'denetim izine yazılır.'
          : ''}
        confirmLabel={settleRequest
          ? settleAction(settleRequest).label : 'Onayla'}
        extra={(
          <input
            value={settleNote}
            onChange={(event) => setSettleNote(event.target.value)}
            placeholder="Gerekçe (isteğe bağlı, ledger notuna yazılır)"
            className="admin-input w-full"
            maxLength={255}
            aria-label="Sonuçlandırma gerekçesi"
          />
        )}
        onCancel={() => { setSettleRequest(null); setSettleNote(''); }}
        onConfirm={runSettle}
      />
    </DetailPage>
  );
};

export default SportsBetDetail;
